/**
 * TanStack Query hooks — Projects.
 *
 * Routes API :
 *   GET    /v1/projects?cursor&q&status&...
 *   POST   /v1/projects
 *   GET    /v1/projects/:id    → ProjectRowDetail (avec submissions_count)
 *   PATCH  /v1/projects/:id
 *   DELETE /v1/projects/:id    → archivage soft
 *
 * Note 8.B/8.D — `null` désormais accepté côté UpdateProjectSchema pour
 * `broker_id` et `contact_id` (D1/D2 résolues). L'UI peut envoyer `null`
 * pour détacher un champ ; `undefined` reste "ne touche pas".
 */

import type {
  CreateProjectInput,
  DbPage,
  GuaranteeCode,
  InsurerCode,
  ProjectListItem,
  ProjectRow,
  ProjectRowDetail,
  UpdateProjectInput,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

export type Project = ProjectRow;
export type ProjectDetail = ProjectRowDetail;
/**
 * 9.BG.1 — la liste porte l'état OPÉRATIONNEL, pas seulement `status`.
 *
 * `ProjectListItem` étend `ProjectRow` des champs dérivés par
 * `app.project_operational` (0082). Le détail (`GET /:id`) garde `ProjectRow`.
 */
export type ProjectListResult = DbPage<ProjectListItem>;

export type ProjectListParams = {
  cursor?: string | null;
  limit?: number;
  q?: string;
  status?: ProjectRow["status"];
  company_id?: string;
  broker_id?: string;
  // 8.D — D3/D4 : filtres array contains côté API.
  guarantee_code?: GuaranteeCode;
  insurer_code?: InsurerCode;
};

function buildListPath(params: ProjectListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  if (params.status) sp.set("status", params.status);
  if (params.company_id) sp.set("company_id", params.company_id);
  if (params.broker_id) sp.set("broker_id", params.broker_id);
  if (params.guarantee_code) sp.set("guarantee_code", params.guarantee_code);
  if (params.insurer_code) sp.set("insurer_code", params.insurer_code);
  const qs = sp.toString();
  return qs ? `/v1/projects?${qs}` : "/v1/projects";
}

export function useProjects(params: ProjectListParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["projects", "list", params],
    queryFn: () => api.get<ProjectListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ["projects", "detail", id],
    queryFn: () => api.get<ProjectDetail>(`/v1/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api.post<Project>("/v1/projects", input as unknown as Record<string, unknown>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects", "list"] });
    },
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) =>
      api.patch<Project>(`/v1/projects/${id}`, input as unknown as Record<string, unknown>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness"] });
      void qc.invalidateQueries({ queryKey: ["projects", "list"] });
      void qc.invalidateQueries({ queryKey: ["projects", "detail", id] });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/projects/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/**
 * Restore = PATCH /v1/projects/:id { status: 'draft' } (9.P.3.B parité
 * Companies Restore). UpdateProjectSchema.status accepte déjà tout
 * `PROJECT_STATUSES` enum — on choisit 'draft' comme valeur de retour
 * sémantique (état initial logique d'une project active). Le broker peut
 * ensuite faire avancer le status manuellement.
 *
 * Inverse de Archive : ramène une project archivée à l'état actif (draft).
 */
export function useRestoreProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch<Project>(`/v1/projects/${id}`, { status: "draft" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects", "list"] });
      void qc.invalidateQueries({ queryKey: ["projects", "detail", id] });
    },
  });
}

/**
 * Le courtier que le serveur proposerait pour un dossier neuf — 9.BD.1.
 *
 * L'écran ne recalcule RIEN : il demande au serveur qui serait assigné et
 * pourquoi, puis l'affiche. Refaire la règle ici — lire `primary_broker_id`,
 * vérifier le rôle de l'utilisateur — produirait une copie qui divergerait un
 * jour de celle qui décide réellement à la création.
 */
export type CourtierParDefaut = {
  broker_id: string | null;
  origin: "company_primary" | "current_user" | null;
};

export function useCourtierParDefaut(companyId: string | undefined) {
  return useQuery({
    queryKey: ["projects", "broker-default", companyId],
    queryFn: () =>
      api.get<CourtierParDefaut>(`/v1/projects/broker-default?company_id=${companyId!}`),
    enabled: !!companyId,
  });
}
